import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Footer } from "./Home";
import { API_URL, IMAGE_PATH } from "../config/AppConfig";
import {
  FaSearch,
  FaTimes,
  FaEdit,
  FaTrash,
  FaPlus,
  FaBoxOpen,
  FaEye,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaBox,
  FaShoppingCart,
  FaCalendarAlt,
  FaSoap,
} from "react-icons/fa";
import "../assets/css/Home.scss";
import Swal from "sweetalert2";

const MyProducts = () => {
  const token = sessionStorage.getItem("token");
  const shouldShowFooter = !token;
  const userId = sessionStorage.getItem("id");
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    const fetchMyProducts = async () => {
      if (!userId) {
        setProducts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${API_URL}/get_my_product`, {
          params: { user_id: userId },
        });

        if (response.data.success) {
          const items = response.data.product_arr !== "NA" ? response.data.product_arr : [];
          setProducts(items);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error("Error fetching my products:", err);
        setError("Unable to load your products right now. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchMyProducts();
  }, [userId]);

  const getStatusInfo = (status) => {
    switch (status) {
      case 0:
        return {
          label: "Pending",
          color: "warning",
          icon: FaClock,
          bgClass: "bg-warning bg-opacity-10",
          textClass: "text-warning",
          badgeClass: "bg-warning",
          borderClass: "border-warning"
        };
      case 1:
        return {
          label: "Approved",
          color: "success",
          icon: FaCheckCircle,
          bgClass: "bg-success bg-opacity-10",
          textClass: "text-success",
          badgeClass: "bg-success",
          borderClass: "border-success"
        };
      case 2:
        return {
          label: "Rejected",
          color: "danger",
          icon: FaTimesCircle,
          bgClass: "bg-danger bg-opacity-10",
          textClass: "text-danger",
          badgeClass: "bg-danger",
          borderClass: "border-danger"
        };
      default:
        return {
          label: "Unknown",
          color: "secondary",
          icon: FaClock,
          bgClass: "bg-secondary bg-opacity-10",
          textClass: "text-secondary",
          badgeClass: "bg-secondary",
          borderClass: "border-secondary"
        };
    }
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  const handleSearchClear = () => setSearchTerm("");
  const handleSearchSubmit = (event) => event.preventDefault();

  const statusCounts = useMemo(() => {
    return {
      all: products.length,
      pending: products.filter(p => p.status === 0).length,
      approved: products.filter(p => p.status === 1).length,
      rejected: products.filter(p => p.status === 2).length,
    };
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (statusFilter !== "all") {
      filtered = filtered.filter(p => p.status === parseInt(statusFilter));
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.product_name?.toLowerCase().includes(term) ||
          p.category_name?.toLowerCase().includes(term) ||
          p.brand_name?.toLowerCase().includes(term) ||
          p.description?.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [products, searchTerm, statusFilter]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToList = () => {
    setSelectedProduct(null);
  };

  const handleEditProduct = (product) => {
    if (!product) return;
    navigate(`/addProduct?productId=${product.product_id}`, {
      state: { editProduct: product },
    });
  };

  const handleDeleteProduct = async (productId) => {
    const confirmResult = await Swal.fire({
      title: 'Mark as Sold Out?',
      text: "Are you sure you want to mark this product as Sold Out?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, mark it!',
      cancelButtonText: 'No, cancel',
      customClass: {
        popup: 'swal-custom-popup',
        confirmButton: 'swal-custom-confirm',
        cancelButton: 'swal-custom-cancel'
      }
    });

    if (!confirmResult.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("product_id", productId);

      const response = await axios.post(`${API_URL}/delete_product`, formData);

      if (response.data.success) {
        setProducts((prev) => prev.filter((p) => p.product_id !== productId));
        setSelectedProduct(null);

        Swal.fire({
          title: 'Marked!',
          text: 'Product marked as Sold Out.',
          icon: 'success',
          confirmButtonColor: '#28a745',
          customClass: {
            popup: 'swal-custom-popup',
            confirmButton: 'swal-custom-confirm'
          }
        });

      } else {
        Swal.fire({
          title: 'Failed!',
          text: response.data.msg || "Unable to update product.",
          icon: 'error',
          confirmButtonColor: '#dc3545',
          customClass: {
            popup: 'swal-custom-popup',
            confirmButton: 'swal-custom-confirm'
          }
        });
      }

    } catch (err) {
      console.error("Error updating product:", err);

      Swal.fire({
        title: 'Error!',
        text: 'Something went wrong. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc3545',
        customClass: {
          popup: 'swal-custom-popup',
          confirmButton: 'swal-custom-confirm'
        }
      });
    }
  };


  if (loading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      }}>
        <div className="text-center">
          <div className="spinner-border text-light mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-white fs-5 fw-medium">Loading your products...</p>
        </div>
      </div>
    );
  }

  if (selectedProduct) {
    return (
      <MyProductDetailsView
        product={selectedProduct}
        onBack={handleBackToList}
        onEdit={handleEditProduct}
        onDelete={handleDeleteProduct}
        token={token}
        getStatusInfo={getStatusInfo}
      />
    );
  }

  return (
    <div className="my-products-page">
      {/* Premium Header Section */}
      <section className="my-products-hero" style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      }}>
        <div style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "url('data:image/svg+xml,%3Csvg width=\"60\" height=\"60\" viewBox=\"0 0 60 60\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cg fill=\"none\" fill-rule=\"evenodd\"%3E%3Cg fill=\"%23ffffff\" fill-opacity=\"0.05\"%3E%3Cpath d=\"M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')",
          opacity: 0.4
        }}></div>
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row align-items-center">
            <div className="col-lg-8">
              <div className="text-white mb-4 mb-lg-0">
                <h1 className="display-4 fw-bold mb-3" style={{ textShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
                  My Product Catalog
                </h1>
                <p className="lead mb-0 opacity-90">
                  Manage your entire product inventory with ease
                </p>
              </div>
            </div>
            <div className="col-lg-4 text-lg-end">
              <button
                className="btn btn-light btn-lg shadow-lg px-5 py-3"
                onClick={() => navigate("/addProduct")}
                style={{
                  borderRadius: "50px",
                  fontWeight: "600",
                  transition: "all 0.3s ease"
                }}
                onMouseOver={(e) => e.target.style.transform = "translateY(-2px)"}
                onMouseOut={(e) => e.target.style.transform = "translateY(0)"}
              >
                <FaPlus className="me-2" />
                Add New Product
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="my-products-hero__stats">
            <div className="my-products-stats-card">
              <h3 className="mb-0 fw-bold text-primary">{statusCounts.all}</h3>
              <small className="text-muted">Total Products</small>
            </div>
            <div className="my-products-stats-card">
              <h3 className="mb-0 fw-bold text-warning">{statusCounts.pending}</h3>
              <small className="text-muted">Pending</small>
            </div>
            <div className="my-products-stats-card">
              <h3 className="mb-0 fw-bold text-success">{statusCounts.approved}</h3>
              <small className="text-muted">Approved</small>
            </div>
            <div className="my-products-stats-card">
              <h3 className="mb-0 fw-bold text-danger">{statusCounts.rejected}</h3>
              <small className="text-muted">Rejected</small>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="my-products-search-section">
        <div className="container">
          <div className="card border-0 shadow-lg">
            <div className="card-body">
              <div className="row g-4 align-items-center">
                {/* Search Box */}
                <div className="col-lg-7">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="position-relative">
                      <FaSearch className="position-absolute text-muted" style={{ left: "20px", top: "50%", transform: "translateY(-50%)", fontSize: "1.2rem" }} />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search by product name, brand, category..."
                        className="form-control form-control-lg border-0 shadow-sm"
                        style={{
                          paddingLeft: "55px",
                          paddingRight: searchTerm ? "50px" : "20px",
                          borderRadius: "50px",
                          background: "#f8f9fa",
                          fontSize: "1rem"
                        }}
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          className="btn btn-link position-absolute"
                          style={{ right: "10px", top: "50%", transform: "translateY(-50%)", color: "#6c757d" }}
                          onClick={handleSearchClear}
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  </form>
                </div>

                {/* Status Filter Buttons */}
                <div className="col-lg-5">
                  <div className="d-flex gap-2 flex-wrap justify-content-lg-end">
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`btn my-products-filter-btn ${statusFilter === "all" ? "btn-primary shadow" : "btn-outline-secondary"}`}
                    >
                      All
                      <span className="badge bg-white text-primary ms-2">{statusCounts.all}</span>
                    </button>
                    <button
                      onClick={() => setStatusFilter("0")}
                      className={`btn my-products-filter-btn ${statusFilter === "0" ? "btn-warning shadow text-white" : "btn-outline-warning"}`}
                    >
                      <FaClock className="me-1" />
                      Pending
                      <span className={`badge ${statusFilter === "0" ? "bg-white text-warning" : "bg-warning text-white"} ms-2`}>
                        {statusCounts.pending}
                      </span>
                    </button>
                    <button
                      onClick={() => setStatusFilter("1")}
                      className={`btn my-products-filter-btn ${statusFilter === "1" ? "btn-success shadow" : "btn-outline-success"}`}
                    >
                      <FaCheckCircle className="me-1" />
                      Approved
                      <span className={`badge ${statusFilter === "1" ? "bg-white text-success" : "bg-success text-white"} ms-2`}>
                        {statusCounts.approved}
                      </span>
                    </button>
                    <button
                      onClick={() => setStatusFilter("2")}
                      className={`btn my-products-filter-btn ${statusFilter === "2" ? "btn-danger shadow" : "btn-outline-danger"}`}
                    >
                      <FaTimesCircle className="me-1" />
                      Rejected
                      <span className={`badge ${statusFilter === "2" ? "bg-white text-danger" : "bg-danger text-white"} ms-2`}>
                        {statusCounts.rejected}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="my-products-grid-section">
        <div className="container">
          {error ? (
            <div className="text-center py-5">
              <div className="mb-4">
                <FaBoxOpen style={{ fontSize: "5rem", color: "#dee2e6" }} />
              </div>
              <h3 className="mb-3">Oops! Something went wrong</h3>
              <p className="text-muted mb-4">{error}</p>
              <button className="btn btn-primary btn-lg px-5" style={{ borderRadius: "50px" }} onClick={() => window.location.reload()}>
                Try Again
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <div className="mb-4">
                {searchTerm || statusFilter !== "all" ? (
                  <FaSearch style={{ fontSize: "5rem", color: "#dee2e6" }} />
                ) : (
                  <FaBoxOpen style={{ fontSize: "5rem", color: "#dee2e6" }} />
                )}
              </div>
              <h3 className="mb-3">
                {searchTerm || statusFilter !== "all" ? "No products found" : "Your catalog is empty"}
              </h3>
              <p className="text-muted mb-4">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search terms or filters"
                  : "Start adding products to build your catalog"}
              </p>
              {searchTerm || statusFilter !== "all" ? (
                <button
                  className="btn btn-primary btn-lg px-5"
                  style={{ borderRadius: "50px" }}
                  onClick={() => {
                    handleSearchClear();
                    setStatusFilter("all");
                  }}
                >
                  Clear All Filters
                </button>
              ) : (
                <button
                  className="btn btn-primary btn-lg px-5"
                  style={{ borderRadius: "50px" }}
                  onClick={() => navigate("/addProduct")}
                >
                  <FaPlus className="me-2" />
                  Add Your First Product
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-bold">
                  {statusFilter === "all" ? "All Products" :
                    statusFilter === "0" ? "Pending Products" :
                      statusFilter === "1" ? "Approved Products" : "Rejected Products"}
                </h4>
                <span className="badge bg-primary px-3 py-2" style={{ borderRadius: "50px", fontSize: "0.9rem" }}>
                  {filteredProducts.length} Products
                </span>
              </div>

              <div className="row g-4">
                {filteredProducts.map((product) => (
                  <div key={product.product_id} className="col-md-6 col-lg-4">
                    <MyProductCard
                      product={product}
                      onClick={() => handleProductClick(product)}
                      onEdit={() => handleEditProduct(product)}
                      onDelete={() => handleDeleteProduct(product.product_id)}
                      getStatusInfo={getStatusInfo}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {shouldShowFooter && <Footer token={token} />}
    </div>
  );
};

const MyProductCard = ({ product, onClick, onEdit, onDelete, getStatusInfo }) => {
  const statusInfo = getStatusInfo(product.status);
  const StatusIcon = statusInfo.icon;

  const imageUrl = product.images !== "NA" && product.images?.[0]?.image
    ? `${IMAGE_PATH}${product.images[0].image}`
    : product.image
      ? `${IMAGE_PATH}${product.image}`
      : "https://via.placeholder.com/400x300?text=No+Image";

  return (
    <div
      className="card my-product-card h-100 border-0 shadow-lg"
      onClick={onClick}
    >
      {/* Image Section with Gradient Overlay */}
      <div
        className="position-relative my-product-card__image"
        onClick={onClick}
      >
        <img
          src={imageUrl}
          alt={product.product_name}
          className="w-100 h-100"
          style={{ objectFit: "cover", transition: "transform 0.5s ease" }}
          onMouseOver={(e) => e.target.style.transform = "scale(1.1)"}
          onMouseOut={(e) => e.target.style.transform = "scale(1)"}
        />

        {/* Gradient Overlay */}
        <div style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "50%",
          background: "linear-gradient(to bottom, transparent, rgba(0,0,0,0.7))"
        }}></div>

        {/* Category Badge */}
        <span className="badge position-absolute my-product-card__badge">
          {product.category_name}
        </span>

        {/* Status Badge with Icon */}
        <span className={`badge ${statusInfo.badgeClass} position-absolute d-flex align-items-center gap-1 my-product-card__status-badge`}>
          <StatusIcon />
          {statusInfo.label}
        </span>

        {/* Brand Name at Bottom */}
        <div className="position-absolute bottom-0 start-0 text-white my-product-card__brand">
          <small className="d-block mb-1">
            {product.brand_name}
          </small>
        </div>
      </div>

      {/* Card Body */}
      <div className="card-body p-4" onClick={onClick}>
        <h5 className="card-title fw-bold mb-2" style={{ fontSize: "1.1rem", minHeight: "52px", lineHeight: "1.4" }}>
          {product.product_name}
        </h5>

        <p className="text-muted small mb-3" style={{ minHeight: "40px", fontSize: "0.9rem" }}>
          {product.description?.length > 70
            ? product.description.substring(0, 70) + "..."
            : product.description}
        </p>

        {/* Stats with Icons */}
        <div className="my-product-card__stats">
          <div className="my-product-card__stat-item">
            <div className="my-product-card__stat-icon" style={{ background: "#e3f2fd" }}>
              <FaBox className="text-primary" />
            </div>
            <div>
              <small className="text-muted d-block my-product-card__stat-label">Available</small>
              <strong className="my-product-card__stat-value">{product.available_qty}</strong>
            </div>
          </div>
          <div className="my-product-card__stat-item">
            <div className="my-product-card__stat-icon" style={{ background: "#fff3e0" }}>
              <FaShoppingCart className="text-warning" />
            </div>
            <div>
              <small className="text-muted d-block my-product-card__stat-label">Min Order</small>
              <strong className="my-product-card__stat-value">{product.min_order_qty}</strong>
            </div>
          </div>
        </div>

        {/* Footer - Price and Actions */}
        <div className="my-product-card__footer">
          <div className="my-product-card__price">
            <small className="text-muted d-block mb-1">Price</small>
            <h4 className="mb-0 fw-bold">
              AED {product.price}
            </h4>
          </div>
          <button
            className="btn btn-light shadow-sm my-product-card__view-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            title="View Details"
          >
            <FaEye className="text-primary" />
          </button>
        </div>
      </div>
    </div>
  );
};

const MyProductDetailsView = ({ product, onBack, onEdit, onDelete, token, getStatusInfo }) => {
  const shouldShowFooter = !token;
  const statusInfo = getStatusInfo(product.status);
  const StatusIcon = statusInfo.icon;

  const images = product.images !== "NA" && Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : [{ product_image_id: 0, image: "placeholder.jpg" }];

  return (
    <div className="my-product-detail-view">
      <div className="container">
        {/* Action Header */}
        <div className="my-product-detail-view__actions">
          <button
            className="btn btn-light shadow-sm"
            onClick={onBack}
          >
            <FaTimes className="me-2" />
            Close
          </button>
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary shadow"
              onClick={() => onEdit(product)}
            >
              <FaEdit className="me-2" />
              Edit
            </button>
            <button
              className="btn btn-danger shadow"
              onClick={() => onDelete(product.product_id)}
            >
              <FaSoap className="me-2" />
              Sold Out
            </button>
          </div>
        </div>

        {/* Product Details Card */}
        <div className="card border-0 shadow-lg" style={{ borderRadius: "25px", overflow: "hidden" }}>
          <div className="row g-0">
            {/* Image Gallery */}
            <div className="col-lg-6">
              <div
                id="productImageCarousel"
                className="carousel slide h-100"
                data-bs-ride="carousel"
                style={{ minHeight: "500px", background: "#f8f9fa" }}
              >
                <div className="carousel-inner h-100">
                  {images.map((img, index) => (
                    <div
                      key={img.product_image_id || index}
                      className={`carousel-item ${index === 0 ? "active" : ""} h-100`}
                    >
                      <img
                        src={img.image !== "placeholder.jpg" ? `${IMAGE_PATH}${img.image}` : "https://via.placeholder.com/600x600?text=No+Image"}
                        className="d-block w-100 h-100"
                        style={{ objectFit: "cover" }}
                        alt={product.product_name}
                      />
                    </div>
                  ))}
                </div>

                {images.length > 1 && (
                  <>
                    <button
                      className="carousel-control-prev"
                      type="button"
                      data-bs-target="#productImageCarousel"
                      data-bs-slide="prev"
                    >
                      <span className="carousel-control-prev-icon" aria-hidden="true" />
                      <span className="visually-hidden">Previous</span>
                    </button>
                    <button
                      className="carousel-control-next"
                      type="button"
                      data-bs-target="#productImageCarousel"
                      data-bs-slide="next"
                    >
                      <span className="carousel-control-next-icon" aria-hidden="true" />
                      <span className="visually-hidden">Next</span>
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {images.length > 1 && (
                  <div
                    className="position-absolute bottom-0 start-50 translate-middle-x mb-3"
                    style={{ zIndex: 10 }}
                  >
                    <span className="badge bg-dark px-3 py-2" style={{ borderRadius: "50px", fontSize: "0.85rem" }}>
                      1 / {images.length}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Product Info */}
            <div className="col-lg-6">
              <div className="my-product-detail-view__content" style={{ height: "100%", overflowY: "auto" }}>
                {/* Header with badges */}
                <div className="my-product-detail-view__header">
                  <div className="d-flex gap-2 mb-3 flex-wrap">
                    <span className="badge" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
                      {product.category_name}
                    </span>
                    <span className={`badge ${statusInfo.badgeClass} d-flex align-items-center gap-2`}>
                      <StatusIcon />
                      {statusInfo.label}
                    </span>
                  </div>
                  <small className="text-muted d-block">{product.brand_name}</small>
                  <h1 className="fw-bold">{product.product_name}</h1>
                </div>

                {/* Price Section */}
                <div className="my-product-detail-view__price-section">
                  <small className="text-muted d-block">Price</small>
                  <h2 className="fw-bold">
                    AED {product.price}
                  </h2>
                </div>

                {/* Description */}
                <div className="my-product-detail-view__section">
                  <h5 className="fw-bold d-flex align-items-center">
                    <span className="badge bg-primary"></span>
                    Description
                  </h5>
                  <p>{product.description}</p>
                </div>

                {/* Quick Stats */}
                <div className="my-product-detail-view__stats-grid">
                  <div className="stat-card" style={{ background: "#e3f2fd" }}>
                    <div className="d-flex align-items-center">
                      <FaBox className="text-primary" />
                      <small className="text-muted">Available Quantity</small>
                    </div>
                    <h4 className="fw-bold">{product.available_qty} <small className="text-muted">units</small></h4>
                  </div>
                  <div className="stat-card" style={{ background: "#fff3e0" }}>
                    <div className="d-flex align-items-center">
                      <FaShoppingCart className="text-warning" />
                      <small className="text-muted">Minimum Order</small>
                    </div>
                    <h4 className="fw-bold">{product.min_order_qty} <small className="text-muted">units</small></h4>
                  </div>
                </div>

                {/* Specifications */}
                <div className="my-product-detail-view__section">
                  <h5 className="fw-bold d-flex align-items-center">
                    <span className="badge bg-primary"></span>
                    Specifications
                  </h5>
                  <div className="list-group list-group-flush my-product-detail-view__specs">
                    <div className="list-group-item border-bottom">
                      <span className="text-muted">Brand</span>
                      <strong>{product.brand_name}</strong>
                    </div>
                    <div className="list-group-item border-bottom">
                      <span className="text-muted">Category</span>
                      <strong>{product.category_name}</strong>
                    </div>
                    {product.specification && (
                      <div className="list-group-item border-bottom">
                        <span className="text-muted">Details</span>
                        <strong>{product.specification}</strong>
                      </div>
                    )}
                    <div className="list-group-item border-bottom">
                      <span className="text-muted d-flex align-items-center gap-2">
                        <FaCalendarAlt />
                        Created Date
                      </span>
                      <strong>{product.createtime}</strong>
                    </div>
                    <div className="list-group-item">
                      <span className="text-muted">Status</span>
                      <span className={`badge ${statusInfo.badgeClass} d-flex align-items-center gap-1`}>
                        <StatusIcon />
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="my-product-detail-view__actions-grid">
                  <button
                    className="btn btn-primary btn-lg shadow"
                    onClick={() => onEdit(product)}
                  >
                    <FaEdit />
                    Edit Product Details
                  </button>
                  <button
                    className="btn btn-outline-danger btn-lg"
                    onClick={() => onDelete(product.product_id)}
                  >
                    <FaSoap />
                    Sold Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {shouldShowFooter && <Footer token={token} />}
    </div>
  );
};

export default MyProducts;